import axios, {type AxiosResponse} from "axios";
import {app, throwError, use_mp3} from "./config.ts";
import {Utils} from "./utils.ts";
import type {ApiResponse} from "./interfaces.ts";
import type {DaoApiAuth} from "../daos/dao-api-auth.ts";
import type {DaoApiChannel} from "../daos/dao-api-channel.ts";
import type {DaoApiFavorite} from "../daos/dao-api-favorite.ts";
import type {DaoApiInit} from "../daos/dao-api-init.ts";
import type {DaoApiAnnounce} from "../daos/dao-api-announce.ts";
import type {DaoApiSchedule} from "../daos/dao-api-schedule.ts";
import type {DaoApiAiVoice} from "../daos/dao-api-ai-voice.ts";
import type {DaoApiBase} from "../daos/dao-api-base.ts";
import type {DaoApiAiVoiceCreate} from "../daos/dao-api-ai-voice-create.ts";
import type {DaoApiConnection} from "../daos/dao-api-connection.ts";
import {store} from "../stores";
import {hidePreloader, showPreloader} from "../stores/loader.slice.ts";
import type {DaoApiChannelData} from "../daos/dao-api-channel-data.ts";
import type {DaoMedia} from "../daos/dao-media.ts";
import type {DaoApiMediaResource} from "../daos/dao-api-media-resource.ts";
import _ from "lodash";
import {isLogin} from "./auth.ts";

export class Api {

  private static headers(cancel: AbortController | null = null, responseType: string = 'json'): object {
    const signal: AbortSignal | null = (!_.isNull(cancel)) ? cancel?.signal : null;

    let accept: string;
    switch (responseType) {
      case 'arraybuffer':
        accept = 'audio/*';
        break;
      case 'json':
        accept = 'application/json';
        break;
      default:
        accept = '*/*';
        break;
    }

    return {
      headers: {
        accept,
        'content-type': 'application/x-www-form-urlencoded',
        // 'authorization': `Bearer ${app.auth}`,
        // 'x-client-type': app.device,
        // 'x-client-id': app.user?.id,
        // 'x-client-token': app.cryptkey,
      },
      withCredentials: true,
      responseType,
      signal
    };
  }

  static parameters(params: object = {}, is_init: boolean = false): object {
    const site = app.site;
    const uuid = app.uuid;
    const ip = is_init ? '' : app.ip;
    const date = Utils.toDateString();
    const nochche = Utils.milliseconds();
    const token = Utils.sha256(`${site}:${uuid}:${ip}:${date}:${nochche}`);
    const payload = Utils.encrypt(
      JSON.stringify(Object.assign(app.getParameters(), params)),
      app.inited?.cryption,
      token.slice(-16)
    );
    return { site, uuid, date, token, nochche, ip, payload };
  }

  static uri(url: string) {
    return app.apiUrl + url;
  }

  static async post(uri: string, parameter: object, preloader: boolean = true, cancel: AbortController | null = null): Promise<AxiosResponse<unknown> | void> {
    if(preloader) store.dispatch(showPreloader());
    const axiosResponse = await axios
      .post(
        uri,
        parameter,
        this.headers(cancel)
      )
      .catch(() => {
        if(preloader) store.dispatch(hidePreloader());
      });
    if(preloader) store.dispatch(hidePreloader());
    return axiosResponse;
  }

  static async get(uri: string, preloader: boolean = true, cancel: AbortController | null = null): Promise<AxiosResponse<unknown> | void> {
    if(preloader) store.dispatch(showPreloader());
    const axiosResponse = await axios
      .get(
        uri,
        this.headers(cancel)
      )
      .catch(() => {
        if(preloader) store.dispatch(hidePreloader());
      });
    if(preloader) store.dispatch(hidePreloader());
    return axiosResponse;
  }

  static async init(cancel: AbortController | null = null): Promise<ApiResponse<DaoApiInit>> {
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/inited.json'),
      this.parameters({}, false),
      false,
      cancel
    ) as ApiResponse<DaoApiInit>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("init: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiInit
    };
  }

  static async login(id: string, password: string, connection: string = '0', cancel: AbortController | null = null): Promise<ApiResponse<DaoApiAuth>> {
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/login.json'),
      this.parameters({id, password, connection}),
      true,
      cancel
    ) as ApiResponse<DaoApiAuth>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("login: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiAuth
    };
  }

  static async logout(reason: string, useBeacon: boolean = false): Promise<void> {
    if(isLogin()) {
      const params = Object.assign(this.parameters()||{}, {reason, beacon: useBeacon});
      const axiosResponse = await this.post(
        this.uri(`/web.player.v2/logout.${useBeacon ? "send" : "json"}`),
        params,
        false
      ) as ApiResponse<DaoApiAuth>;

      if(!useBeacon) {
        if (!axiosResponse || !axiosResponse.data) {
          throwError("login: Invalid response or data is missing");
        }
      }
    }
  }

  static async channel(category: string, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiChannel>> {
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/channel.json'),
      this.parameters({category}),
      true,
      cancel
    ) as ApiResponse<DaoApiChannel>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("channel: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiChannel
    };
  }

  static async channel_data(no: string, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiChannelData>> {
    const params: {[key: string]: any} = {no};
    if(app.cfg.exposePath) params.exposed = 1;
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/channel.data.json'),
      this.parameters(params),
      true,
      cancel
    ) as ApiResponse<DaoApiChannelData>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("channel_data: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiChannelData
    };
  }

  static async channel_clear(cancel: AbortController | null = null): Promise<ApiResponse<DaoApiBase>> {
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/channel.clear.json'),
      this.parameters(),
      true,
      cancel
    ) as ApiResponse<DaoApiBase>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("channel_clear: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiBase
    };
  }

  static async favorite(no: string, favorite: number, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiFavorite>> {
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/favorite.json'),
      this.parameters({no, favorite}),
      true,
      cancel
    ) as ApiResponse<DaoApiFavorite>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("favorite: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiFavorite
    };
  }

  static async connection(cancel: AbortController | null = null): Promise<ApiResponse<DaoApiConnection>> {
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/connection.json'),
      this.parameters(),
      false,
      cancel
    ) as ApiResponse<DaoApiConnection>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("connection: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiConnection
    };
  }

  static async schedule(date: string, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiSchedule>> {
    const params: {[key: string]: any} = {date};
    if(app.cfg.exposePath) params.exposed = 1;

    const axiosResponse = await this.post(
      this.uri('/web.player.v2/schedule.json'),
      this.parameters(params),
      true,
      cancel
    ) as ApiResponse<DaoApiSchedule>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("schedule: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiSchedule
    };
  }

  static async announces(cancel: AbortController | null = null): Promise<ApiResponse<DaoApiAnnounce>> {
    const params: {[key: string]: any} = {};
    if(app.cfg.exposePath) params.exposed = 1;

    const axiosResponse = await this.post(
      this.uri('/web.player.v2/advertise.json'),
      this.parameters(params),
      true,
      cancel
    ) as ApiResponse<DaoApiAnnounce>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("announces: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiAnnounce
    };
  }

  static async ai_voice(page: number = 1, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiAiVoice>> {
    const params: {[key: string]: any} = {page};
    if(app.cfg.exposePath) params.exposed = 1;

    const axiosResponse = await this.post(
      this.uri('/web.player.v2/ai.voice.list.json'),
      this.parameters(params),
      true,
      cancel
    ) as ApiResponse<DaoApiAiVoice>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("ai_voice: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiAiVoice
    };
  }

  static async ai_voice_delete(tts: number, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiBase>> {
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/ai.voice.delete.json'),
      this.parameters({tts}),
      true,
      cancel
    ) as ApiResponse<DaoApiBase>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("ai_voice_delete: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiBase
    };
  }

  static async ai_voice_create(voice: string, message: string, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiAiVoiceCreate>> {
    const params: {[key: string]: any} = {voice, message};
    if(app.cfg.exposePath) params.exposed = 1;
    const axiosResponse = await this.post(
      this.uri('/web.player.v2/ai.voice.create.json'),
      this.parameters(params),
      true,
      cancel
    ) as ApiResponse<DaoApiAiVoiceCreate>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("ai_voice_create: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiAiVoiceCreate
    };
  }

  /**
   * 리턴안됨
   * @param media
   * @param cancel
   */
  static async logs(media: DaoMedia|null, cancel: AbortController | null = null): Promise<void> {
    if(!media) return;

    console.log("logs", media);

    await this.post(
      this.uri('/web.player.v2/logs.send'),
      this.parameters({media}),
      false,
      cancel
    );

    // const axiosResponse = await this.post(
    //   this.uri('/web.player.v2/logs.send'),
    //   this.parameters({media}),
    //   false,
    //   cancel
    // ) as ApiResponse<object>;
    // if (!axiosResponse || !axiosResponse.data) {
    //   throwError("logs: Invalid response or data is missing");
    // }
    //
    // return {
    //   ...axiosResponse,
    //   data: axiosResponse.data as object
    // };
  }

  static async media_resource(media: DaoMedia, isAdvertise: boolean = false, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiMediaResource>> {
    const url: string = `${isAdvertise ? app.streams[0] : app.streamUrl}/stream/auth/${media.stream}/${Utils.pad(media.media, 10)}`;

    const parameters: {[key: string]: any } = app.getParameters();
    if(media.m4a) {
      parameters.m4a = media.m4a;
      parameters.use_mp3 = use_mp3;
    }
    if(media.refresh) parameters.refresh = media.refresh;

    const query: string = Utils.base64encode(JSON.stringify(parameters));
    const axiosResponse = await this.get(
      `${url}?${query}`,
      false,
      cancel
    ) as ApiResponse<DaoApiMediaResource>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("media_resource: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiMediaResource
    };
  }

  static async media_standby(media: DaoMedia, cancel: AbortController | null = null): Promise<ApiResponse<DaoApiMediaResource>> {
    const url: string = `${app.streamUrl}/stream/standby/${media.stream}/${Utils.pad(media.media, 10)}`;

    const parameters: {[key: string]: any } = app.getParameters();
    if(media.m4a) {
      parameters.m4a = media.m4a;
      parameters.use_mp3 = use_mp3;
    }
    const query: string = Utils.base64encode(JSON.stringify(parameters));
    const axiosResponse = await this.get(
      `${url}?${query}`,
      false,
      cancel
    ) as ApiResponse<DaoApiMediaResource>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("media_standby: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as DaoApiMediaResource
    };
  }

  static async media(url: string, cancel: AbortController | null = null): Promise<ApiResponse<ArrayBuffer>> {
    const axiosResponse = await axios.get(
      url,
      this.headers(cancel, 'arraybuffer')
    ) as ApiResponse<ArrayBuffer>;

    if (!axiosResponse || !axiosResponse.data) {
      throwError("media: Invalid response or data is missing");
    }

    return {
      ...axiosResponse,
      data: axiosResponse.data as ArrayBuffer
    };
  }
}